import { i18n, isLocale, parseLocale, type Locale } from "@/i18n-config";

export type ResolveAuthEmailLocaleArgs = {
  isRegisteredUser: boolean;
  profileLanguage?: string | null;
  websiteLanguage?: string | null;
};

export type ResolveHookEmailLocaleArgs = {
  action: "signup" | "recovery" | "email_change";
  profileLanguage?: string | null;
  websiteLanguage?: string | null;
};

export function toSupportedLocale(value: string | null | undefined): Locale {
  return parseLocale(value) ?? i18n.defaultLocale;
}

export function parseLocaleFromPath(urlOrPath: string | null | undefined): Locale | null {
  if (!urlOrPath || urlOrPath.trim().length === 0) return null;

  const normalized = urlOrPath.trim();
  let pathname = normalized;

  if (normalized.startsWith("http://") || normalized.startsWith("https://")) {
    try {
      pathname = new URL(normalized).pathname;
    } catch {
      return null;
    }
  } else if (!normalized.startsWith("/")) {
    pathname = `/${normalized}`;
  }

  const first = pathname.split("/").filter(Boolean)[0];
  if (!first) return null;
  return isLocale(first) ? first : null;
}

export function parseLocaleFromRedirectTarget(urlOrPath: string | null | undefined): Locale | null {
  const direct = parseLocaleFromPath(urlOrPath);
  if (direct) return direct;
  if (!urlOrPath || urlOrPath.trim().length === 0) return null;

  const normalized = urlOrPath.trim();
  let parsedUrl: URL;
  try {
    if (normalized.startsWith("http://") || normalized.startsWith("https://")) {
      parsedUrl = new URL(normalized);
    } else {
      parsedUrl = new URL(normalized, "http://localhost");
    }
  } catch {
    return null;
  }

  const candidateKeys = ["next", "redirect_to", "redirectTo", "returnTo"];
  for (const key of candidateKeys) {
    const raw = parsedUrl.searchParams.get(key);
    const candidateLocale = parseLocaleFromPath(raw);
    if (candidateLocale) return candidateLocale;
  }

  return null;
}

function toOptionalSupportedLocale(value: string | null | undefined): Locale | null {
  return parseLocale(value);
}

export function resolveAuthEmailLocale(args: ResolveAuthEmailLocaleArgs): Locale {
  if (args.isRegisteredUser) {
    return toSupportedLocale(args.profileLanguage);
  }

  return toSupportedLocale(args.websiteLanguage);
}

export function resolveHookEmailLocale(args: ResolveHookEmailLocaleArgs): Locale {
  const profileLocale = toOptionalSupportedLocale(args.profileLanguage);
  const websiteLocale = toOptionalSupportedLocale(args.websiteLanguage);

  if (args.action === "signup") {
    return websiteLocale ?? i18n.defaultLocale;
  }

  if (args.action === "recovery") {
    return websiteLocale ?? profileLocale ?? i18n.defaultLocale;
  }

  return profileLocale ?? websiteLocale ?? i18n.defaultLocale;
}
